// REST adapter — used for any externally hosted target (DigitalOcean, Azure,
// AWS, GCP, Cloudflare). The SPA holds an OIDC access token (issued by
// NetSuite as the OIDC provider) in sessionStorage and sends it as the
// Authorization: Bearer header. The backend BFF validates the token and
// proxies to NetSuite RESTlets / SuiteTalk REST.

import { config } from "../config";
import { getAccessToken, refresh, beginLogin } from "../auth/oidc";

async function authHeader() {
  let token = getAccessToken();
  if (!token) {
    const r = await refresh().catch(() => null);
    token = r?.accessToken || null;
  }
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function call(method, path, body, retried = false) {
  const url = `${config.apiBase}${path}`;
  const headers = {
    "Content-Type": "application/json",
    ...(await authHeader()),
  };
  const res = await fetch(url, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && !retried) {
    const r = await refresh().catch(() => null);
    if (r?.accessToken) return call(method, path, body, true);
    await beginLogin(window.location.pathname + window.location.search);
    throw new Error("unauthenticated");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${method} ${path} failed: ${res.status} ${text}`);
  }
  if (res.status === 204) return null;
  // In dev, /api/* routes have no backend and Vite returns the SPA shell.
  // Detect the HTML response and reject so the controllers' mock fallbacks fire.
  const ctype = res.headers.get("content-type") || "";
  if (!ctype.includes("application/json")) {
    throw new Error(`no_api: ${method} ${path} returned ${ctype || "unknown"}`);
  }
  return res.json();
}

export const restAdapter = {
  name: "rest",

  // Auth — handled by the OIDC module. These exist so the adapter surface is
  // identical to the embedded one; the AuthContext calls into oidc.js
  // directly for begin/complete/logout.
  async getSession() { return call("GET", "/session"); },
  async login() { throw new Error("use oidc.beginLogin instead"); },
  async logout() { return call("POST", "/auth/logout").catch(() => null); },

  async listProducts(params) {
    const qs = params ? "?" + new URLSearchParams(params) : "";
    return call("GET", `/catalog${qs}`);
  },
  async getProduct(id) { return call("GET", `/catalog/${encodeURIComponent(id)}`); },

  async getAvailability(sku, mode) {
    const qs = mode ? `?mode=${encodeURIComponent(mode)}` : "";
    return call("GET", `/availability/${encodeURIComponent(sku)}${qs}`);
  },

  async getCart() { return call("GET", "/cart"); },
  async updateCart(items) { return call("PUT", "/cart", { items }); },

  async listQuotes() { return call("GET", "/quotes"); },
  async getQuote(id) { return call("GET", `/quotes/${encodeURIComponent(id)}`); },
  async createQuote(payload) { return call("POST", "/quotes", payload); },
  async submitQuote(id) { return call("POST", `/quotes/${encodeURIComponent(id)}/submit`); },

  async listOrders() { return call("GET", "/orders"); },
  async getOrder(id) { return call("GET", `/orders/${encodeURIComponent(id)}`); },
  async convertQuoteToOrder(quoteId) {
    return call("POST", `/orders/from-quote/${encodeURIComponent(quoteId)}`);
  },
  async submitSalesOrder(payload)    { return call("POST", "/orders/sales-order", payload); },
  async submitPurchaseOrder(payload) { return call("POST", "/orders/purchase-order", payload); },

  async getAccount() { return call("GET", "/account"); },
  async updateAccount(patch) { return call("PATCH", "/account", patch); },

  // Bolt — server creates the cart and returns the SPA token.
  async createBoltCart(payload) { return call("POST", "/bolt/cart", payload); },
  // Stripe — server returns a PaymentIntent client secret.
  async createStripeIntent(payload) { return call("POST", "/stripe/intent", payload); },
};
